import React, {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Loading} from '../../../components/Loading/Loading'
import {Input} from '../../../components/Input/Input'
import {Button} from '../../../components/Button/Button'
import {POST} from '../../../services/api'
import {i18n} from '../../../translate/i18n'
import TrendChart from './components/TrendChart'

export type AnalyticsFilter = {
  startDate?: string
  endDate?: string
  formId?: number
}

interface DashboardState {
  data: any | null
  loading: boolean
  error: string | null
  filters: AnalyticsFilter
}

const Content: FC = () => {
  const [state, setState] = useState<DashboardState>({
    data: null,
    loading: true,
    error: null,
    filters: {},
  })

  const [formOptions, setFormOptions] = useState<Array<{value: number | string; select: string}>>(
    []
  )
  const [dateStart, setDateStart] = useState<string>('')
  const [dateEnd, setDateEnd] = useState<string>('')
  const [selectedFormId, setSelectedFormId] = useState<number | ''>('')

  // Novo estado para controlar o agrupamento do gráfico
  const [timeGrouping, setTimeGrouping] = useState<'day' | 'month' | 'year'>('day')

  useEffect(() => {
    fetchFormsForFilter()
    loadAnalytics({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchFormsForFilter = async () => {
    try {
      const res = await POST('/feedbackform/filter', {})
      if (res.dados) {
        const options = res.dados.map((f: any) => ({
          value: f.id,
          select: f.title,
        }))
        setFormOptions([
          {
            value: '',
            select: i18n.t('feedback_reviews.filters.all_forms') || 'Todos os formulários',
          },
          ...options,
        ])
      }
    } catch (e) {
      console.error('Erro ao buscar formulários para filtro', e)
    }
  }

  const loadAnalytics = async (filters: AnalyticsFilter) => {
    setState((prev) => ({...prev, loading: true, error: null, filters}))

    try {
      const response = await POST('/feedback-review-hotel/analytics/dashboard', {
        dateFrom: filters.startDate,
        dateTo: filters.endDate,
        feedBackFormId: filters.formId,
      })

      if (response.success && response.data) {
        setState((prev) => ({...prev, data: response.data, loading: false}))
      } else {
        throw new Error(response.message || 'Erro ao carregar analytics')
      }
    } catch (error: any) {
      setState((prev) => ({...prev, loading: false, error: error.message}))
    }
  }

  const handleApplyFilters = () => {
    const newFilters: AnalyticsFilter = {}
    if (dateStart) newFilters.startDate = new Date(`${dateStart}T00:00:00`).toISOString()
    if (dateEnd) newFilters.endDate = new Date(`${dateEnd}T23:59:59.999`).toISOString()
    if (selectedFormId) newFilters.formId = Number(selectedFormId)
    loadAnalytics(newFilters)
  }

  const handleClearFilters = () => {
    setDateStart('')
    setDateEnd('')
    setSelectedFormId('')
    loadAnalytics({})
  }

  const getFriendlyTypeName = (type: string) => {
    const typeDict: Record<string, string> = {
      checkbox: 'Múltipla Escolha',
      radio: 'Seleção Única',
      select: 'Lista Suspensa',
      stars: 'Avaliação',
      text: 'Texto Curto',
      textarea: 'Texto Longo',
    }
    return typeDict[type] || type
  }

  // Função para agrupar os dados do gráfico por Dia, Mês ou Ano
  const getAggregatedTrendData = () => {
    if (!state.data?.trendData) return []

    const grouped = state.data.trendData.reduce((acc: any, curr: any) => {
      let key = curr.date // YYYY-MM-DD
      if (timeGrouping === 'month') key = curr.date.substring(0, 7) // YYYY-MM
      if (timeGrouping === 'year') key = curr.date.substring(0, 4) // YYYY

      if (!acc[key]) {
        acc[key] = {
          date: key,
          totalReviews: 0,
          validRatings: 0,
          sumRating: 0,
          promoters: 0,
          detractors: 0,
        }
      }

      acc[key].totalReviews += curr.totalReviews

      // Usa apenas avaliações válidas (com nota) para a matemática
      const validCount = curr.validRatings !== undefined ? curr.validRatings : curr.totalReviews

      acc[key].validRatings += validCount
      acc[key].sumRating += curr.averageRating * validCount
      acc[key].promoters += curr.promoters || 0
      acc[key].detractors += curr.detractors || 0

      return acc
    }, {})

    return Object.values(grouped)
      .map((g: any) => {
        // Aplica a fórmula real do NPS apenas em cima das notas válidas
        let nps = 0
        if (g.validRatings > 0) {
          nps = Math.round(((g.promoters - g.detractors) / g.validRatings) * 100)
        }

        return {
          date: g.date,
          totalReviews: g.totalReviews,
          averageRating: g.validRatings > 0 ? g.sumRating / g.validRatings : 0,
          npsScore: nps,
        }
      })
      .sort((a: any, b: any) => a.date.localeCompare(b.date))
  }

  const renderDynamicQuestionMetric = (q: any, index: number) => {
    if (!q.metrics || (!q.metrics.distribution && q.metrics.averageScore === undefined)) return null

    // 1. Layout para Avaliações (Estrelas)
    if (q.type === 'stars') {
      return (
        <div key={`${q.order}-${index}`} className='mb-6 pb-6 border-bottom border-gray-200'>
          <div className='d-flex align-items-center justify-content-between mb-4'>
            <div className='d-flex align-items-center gap-3'>
              <h5 className='text-dark fw-bolder m-0'>{q.label}</h5>
              <span className='badge badge-light-warning fw-bold'>
                {getFriendlyTypeName(q.type)}
              </span>
            </div>
            <span className='text-muted fs-8 fw-bold bg-light px-3 py-1 rounded'>
              {q.metrics.totalResponses} respostas
            </span>
          </div>

          <div className='d-inline-flex align-items-center mt-2 px-4 py-3'>
            <i className='fas fa-star fs-2 text-warning me-3'></i>
            <span className='fs-2 fw-bolder text-warning'>
              {q.metrics.averageScore?.toFixed(1) || '0.0'}{' '}
              <span className='fs-6 text-warning opacity-75 fw-bold'>
                / {q.metrics.averageScore > 5 ? '10' : '5'}
              </span>
            </span>
          </div>
        </div>
      )
    }

    // 2. Layout para Textos Abertos (Comentários e Sugestões)
    if (q.type === 'text' || q.type === 'textarea') {
      return (
        <div key={`${q.order}-${index}`} className='mb-6 pb-6 border-bottom border-gray-200'>
          <div className='d-flex align-items-center justify-content-between mb-4'>
            <div className='d-flex align-items-center gap-3'>
              <h5 className='text-dark fw-bolder m-0'>{q.label}</h5>
              <span className='badge badge-light-info fw-bold'>{getFriendlyTypeName(q.type)}</span>
            </div>
            <span className='text-muted fs-8 fw-bold bg-light px-3 py-1 rounded'>
              {q.metrics.totalResponses} respostas
            </span>
          </div>

          {q.metrics.distribution && Object.keys(q.metrics.distribution).length > 0 ? (
            <div className='d-flex flex-column gap-3 mt-4'>
              {Object.entries(q.metrics.distribution)
                .sort(([, valA], [, valB]) => (valB as number) - (valA as number))
                .map(([textComment, count], i) => (
                  <div
                    key={i}
                    className='bg-light-info rounded p-4 border border-info border-dashed'
                  >
                    <div className='text-gray-800 fs-6 fst-italic'>"{textComment}"</div>
                    {Number(count) > 1 && (
                      <div className='text-info fw-bolder fs-8 mt-2'>
                        <i className='fas fa-reply me-1 text-info'></i> Citado {String(count)} vezes
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className='text-muted fs-7 mt-2'>Nenhuma resposta em texto registrada.</div>
          )}
        </div>
      )
    }

    // 3. Layout Padrão para Opções (Barra de Progresso)
    return (
      <div key={`${q.order}-${index}`} className='mb-6 pb-6 border-bottom border-gray-200'>
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div className='d-flex align-items-center gap-3'>
            <h5 className='text-dark fw-bolder m-0'>{q.label}</h5>
            <span className='badge badge-light-primary fw-bold'>{getFriendlyTypeName(q.type)}</span>
          </div>
          <span className='text-muted fs-8 fw-bold bg-light px-3 py-1 rounded'>
            {q.metrics.totalResponses} respostas
          </span>
        </div>

        {q.metrics.distribution && Object.keys(q.metrics.distribution).length > 0 ? (
          <div className='d-flex flex-column gap-3 mt-4'>
            {Object.entries(q.metrics.distribution)
              .sort(([, valA], [, valB]) => (valB as number) - (valA as number))
              .map(([label, count]) => {
                const total = q.metrics.totalResponses || 1
                const percent = Math.round(((count as number) / total) * 100)

                return (
                  <div key={label} className='d-flex align-items-center'>
                    <div className='w-200px text-truncate fw-bold text-gray-700 me-3' title={label}>
                      {label}
                    </div>
                    <div className='flex-grow-1 me-4'>
                      <div className='progress h-6px bg-light-primary'>
                        <div
                          className='progress-bar bg-primary rounded'
                          role='progressbar'
                          style={{width: `${percent}%`}}
                        ></div>
                      </div>
                    </div>
                    <div className='w-50px text-end fw-bolder text-gray-600 fs-7'>{percent}%</div>
                  </div>
                )
              })}
          </div>
        ) : (
          <div className='text-muted fs-7 mt-2'>Nenhuma resposta padronizada registrada.</div>
        )}
      </div>
    )
  }

  if (state.loading) return <Loading />

  const chartData = getAggregatedTrendData()

  return (
    <div className='row g-5 gx-xxl-12'>
      <div className='card card-xxl-stretch mb-5 mb-xl-12'>
        <div className='card-header py-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Dashboard Analytics</span>
            <BreadCrumbs />
            <span className='text-muted mt-1 fw-bold fs-7'>
              Métricas extraídas do comportamento dos usuários
            </span>
          </h3>
          <div className='card-toolbar'>
            {state.data && (
              <Button
                text='Exportar Relatório'
                color='success'
                iconColor='#FFF'
                icon='fas fa-file-excel'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => console.log('A implementar exportação')}
                btnClass='fw-bolder'
              />
            )}
          </div>
        </div>

        <div className='card-body border-bottom'>
          {state.error && (
            <div className='alert alert-danger d-flex align-items-center p-5 mb-10'>
              <i className='fas fa-exclamation-triangle fs-2hx text-danger me-4'></i>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Erro ao carregar dados</h4>
                <span>{state.error}</span>
              </div>
            </div>
          )}

          <div className='row g-3 mt-2'>
            <div className='col-md-4 mt-0'>
              <Input
                label='Formulário'
                inputAttr={{type: 'select'}}
                value={selectedFormId}
                change={(v: number) => setSelectedFormId(v)}
                options={
                  formOptions.length > 0 ? formOptions : [{value: '', select: 'Carregando...'}]
                }
              />
            </div>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data Inicial'
                inputAttr={{type: 'date'}}
                value={dateStart}
                change={setDateStart}
                marginTop='0'
              />
            </div>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data Final'
                inputAttr={{type: 'date'}}
                value={dateEnd}
                change={setDateEnd}
                marginTop='0'
              />
            </div>
            <div className='col-12 d-flex justify-content-end mt-4'>
              <Button
                text='Limpar'
                color='danger'
                iconColor='#FFF'
                size='small'
                horizontalPadding={4}
                btnAttr={{type: 'button'}}
                click={handleClearFilters}
                btnClass='me-2'
              />
              <Button
                text='Filtrar'
                color='primary'
                icon='fas fa-filter'
                iconColor='#FFF'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleApplyFilters}
                btnClass='mb-0 fw-bolder'
              />
            </div>
          </div>
        </div>

        <div className='card-body bg-light'>
          {state.data && (
            <>
              {/* Visão Geral */}
              <div className='row g-5 mb-8'>
                <div className='col-xl-4'>
                  <div className='card bg-body shadow-sm border-0 h-100'>
                    <div className='card-body d-flex flex-column justify-content-center'>
                      <div className='d-flex align-items-center justify-content-between mb-4'>
                        <span className='text-gray-600 fw-bold fs-5'>Total de Respostas</span>
                        <i className='fas fa-users fs-2x text-primary'></i>
                      </div>
                      <span className='text-dark fw-bolder fs-2tx'>{state.data.totalReviews}</span>
                    </div>
                  </div>
                </div>

                <div className='col-xl-4'>
                  <div className='card bg-body shadow-sm border-0 h-100'>
                    <div className='card-body d-flex flex-column justify-content-center'>
                      <div className='d-flex align-items-center justify-content-between mb-4'>
                        <span className='text-gray-600 fw-bold fs-5'>Média Geral</span>
                        <i className='fas fa-star-half-alt fs-2x text-warning'></i>
                      </div>
                      <div className='d-flex align-items-baseline'>
                        <span className='text-dark fw-bolder fs-2tx'>
                          {state.data.averageRating > 0
                            ? state.data.averageRating.toFixed(1)
                            : 'N/A'}
                        </span>
                        {state.data.averageRating > 0 && (
                          <span className='text-muted fw-bold fs-3 ms-2'>/ 10</span>
                        )}
                      </div>
                      <span className='text-muted fs-8 mt-1'>Escala padronizada para NPS</span>
                    </div>
                  </div>
                </div>

                <div className='col-xl-4'>
                  <div
                    className={`card ${
                      state.data.npsSummary.npsScore >= 0 ? 'bg-light-success' : 'bg-light-danger'
                    } border-0 h-100`}
                  >
                    <div className='card-body'>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <span
                          className={`${
                            state.data.npsSummary.npsScore >= 0 ? 'text-success' : 'text-danger'
                          } fw-bold fs-5`}
                        >
                          Score NPS
                          <i
                            className='fas fa-info-circle ms-2 fs-7 text-muted'
                            title='Termômetro de satisfação.'
                            data-bs-toggle='tooltip'
                          ></i>
                        </span>
                        <i
                          className={`fas fa-tachometer-alt fs-2 ${
                            state.data.npsSummary.npsScore >= 0 ? 'text-success' : 'text-danger'
                          } opacity-50`}
                        ></i>
                      </div>
                      <span className='text-dark fw-bolder fs-2tx'>
                        {state.data.npsSummary.promotersCount === 0 &&
                        state.data.npsSummary.detractorsCount === 0 &&
                        state.data.npsSummary.passivesCount === 0 ? (
                          <span className='text-muted'>N/A</span>
                        ) : (
                          state.data.npsSummary.npsScore
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribuição de Notas */}
              <div className='row g-5 mb-8'>
                <div className='col-xl-12'>
                  <div className='card border border-gray-300 shadow-none'>
                    <div className='card-header pt-5 border-0'>
                      <h3 className='card-title flex-column m-0'>
                        <span className='card-label fw-bolder text-dark'>
                          Distribuição de Notas (0 a 10)
                        </span>
                      </h3>
                    </div>
                    <div className='card-body pt-5'>
                      {state.data.ratingDistribution
                        .filter((item: any) => item.count > 0)
                        .sort((a: any, b: any) => b.score - a.score)
                        .map((item: any) => (
                          <div key={item.score} className='d-flex align-items-center mb-4'>
                            <div className='w-40px fw-bolder text-gray-800'>
                              {item.score} <i className='fas fa-star text-warning fs-8'></i>
                            </div>
                            <div className='flex-grow-1 mx-3'>
                              <div className='progress h-8px bg-light-primary'>
                                <div
                                  className='progress-bar bg-primary rounded'
                                  role='progressbar'
                                  style={{width: `${item.percentage}%`}}
                                ></div>
                              </div>
                            </div>
                            <div className='w-40px text-end fw-bolder text-muted'>{item.count}</div>
                          </div>
                        ))}
                      {state.data.ratingDistribution.every((item: any) => item.count === 0) && (
                        <div className='text-center text-muted py-6'>
                          Nenhuma nota numérica recebida.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='col-xl-12'>
                  <div className='card border border-gray-300 shadow-none'>
                    <div className='card-header pt-5 border-0 align-items-center d-flex justify-content-between'>
                      <h3 className='card-title flex-column m-0'>
                        <span className='card-label fw-bolder text-dark'>Evolução Histórica</span>
                        <span className='text-muted mt-1 fw-bold fs-7'>
                          Volume de respostas e variação da média/NPS
                        </span>
                      </h3>

                      {/* Botões de Agrupamento */}
                      <div className='card-toolbar'>
                        <div className='btn-group' role='group'>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              timeGrouping === 'day' ? 'btn-primary' : 'btn-light-primary'
                            }`}
                            onClick={() => setTimeGrouping('day')}
                          >
                            Dia
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              timeGrouping === 'month' ? 'btn-primary' : 'btn-light-primary'
                            }`}
                            onClick={() => setTimeGrouping('month')}
                          >
                            Mês
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              timeGrouping === 'year' ? 'btn-primary' : 'btn-light-primary'
                            }`}
                            onClick={() => setTimeGrouping('year')}
                          >
                            Ano
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className='card-body'>
                      {/* Gráfico */}
                      {chartData && chartData.length > 0 ? (
                        <div style={{minHeight: '350px'}}>
                          {TrendChart ? (
                            <TrendChart data={chartData} grouping={timeGrouping} />
                          ) : (
                            <span className='text-muted'>Gráfico a implementar</span>
                          )}
                        </div>
                      ) : (
                        <div className='text-center text-muted py-10'>
                          Dados insuficientes para gerar a curva de tendência.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela Resumo */}
              <div className='row g-5 mb-8'>
                <div className='col-xl-12'>
                  <div className='card border border-gray-300 shadow-sm'>
                    <div className='card-header pt-5 border-0'>
                      <h3 className='card-title flex-column m-0'>
                        <span className='card-label fw-bolder text-dark'>
                          Desempenho por Formulário
                        </span>
                      </h3>
                    </div>
                    <div className='card-body py-3'>
                      <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                          <thead>
                            <tr className='fw-bolder text-muted bg-light'>
                              <th className='ps-4 rounded-start'>Nome</th>
                              <th className='text-center'>Respostas</th>
                              <th className='text-center'>Média</th>
                              <th className='text-center'>NPS</th>
                              <th className='text-center rounded-end'>Detratores</th>
                            </tr>
                          </thead>
                          <tbody>
                            {state.data.allForms.map((form: any) => {
                              const hasNumeric =
                                form.averageRating > 0 || form.npsSummary.detractorsCount > 0
                              return (
                                <tr key={form.formId}>
                                  <td className='ps-4 fw-bolder text-gray-800'>
                                    {form.formTitle}{' '}
                                    <span className='text-muted fs-8 fw-normal ms-2'>
                                      (ID: {form.formId})
                                    </span>
                                  </td>
                                  <td className='text-center fw-bolder'>{form.totalReviews}</td>
                                  <td className='text-center'>
                                    {form.averageRating > 0 ? (
                                      <span className='badge badge-light-warning fs-6 d-inline-flex align-items-center'>
                                        <i className='fas fa-star text-warning me-2 fs-8'></i>
                                        {form.averageRating.toFixed(1)}
                                        <span className='text-muted ms-1 fs-8 opacity-75'>
                                          {' '}
                                          / 10
                                        </span>
                                      </span>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td className='text-center'>
                                    {hasNumeric ? (
                                      <span
                                        className={`badge ${
                                          form.npsSummary.npsScore >= 0
                                            ? 'badge-light-success'
                                            : 'badge-light-danger'
                                        } fs-6`}
                                      >
                                        {form.npsSummary.npsScore}
                                      </span>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td className='text-center fw-bolder text-danger'>
                                    {hasNumeric ? `${form.npsSummary.detractorsPercentage}%` : '-'}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ACCORDION: Comportamento por Pergunta */}
              <div className='card border border-gray-300 shadow-sm'>
                <div className='card-header pt-5 border-0 mb-5'>
                  <h3 className='card-title flex-column m-0'>
                    <span className='card-label fw-bolder text-dark'>Respostas Detalhadas</span>
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      Métricas de opções selecionadas e avaliações separadas por questionário
                    </span>
                  </h3>
                </div>

                <div className='card-body pt-0'>
                  <div className='accordion accordion-icon-toggle' id='accordionMetrics'>
                    {state.data.allForms.map((form: any, idx: number) => {
                      const hasMetrics =
                        form.questions &&
                        form.questions.some(
                          (q: any) =>
                            q.metrics?.distribution || q.metrics?.averageScore !== undefined
                        )

                      return (
                        <div
                          className='accordion-item mb-5 border-top border-gray-200'
                          key={`acc-${form.formId}`}
                        >
                          <h2 className='accordion-header' id={`heading-${idx}`}>
                            <button
                              className='accordion-button collapsed py-4'
                              type='button'
                              data-bs-toggle='collapse'
                              data-bs-target={`#collapse-${idx}`}
                            >
                              <div className='d-flex align-items-center justify-content-between w-100 me-4'>
                                <div className='d-flex align-items-center'>
                                  <i className='fas fa-file-alt text-primary fs-3 me-3'></i>
                                  <span className='fw-bolder fs-4 text-dark'>{form.formTitle}</span>
                                </div>
                                <span className='badge badge-light-primary'>
                                  {form.totalReviews} respostas
                                </span>
                              </div>
                            </button>
                          </h2>
                          <div
                            id={`collapse-${idx}`}
                            className='accordion-collapse collapse'
                            data-bs-parent='#accordionMetrics'
                          >
                            <div className='accordion-body bg-white pt-8 pb-2'>
                              {hasMetrics ? (
                                form.questions.map((q: any, qIndex: number) =>
                                  renderDynamicQuestionMetric(q, qIndex)
                                )
                              ) : (
                                <div className='text-muted py-4 text-center'>
                                  Nenhuma questão de múltipla escolha para analisar neste
                                  formulário.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </>
          )}

          {!state.loading && !state.data && !state.error && (
            <div className='alert alert-info d-flex align-items-center p-5 mt-8'>
              <i className='fas fa-info-circle fs-2hx text-info me-4'></i>
              <span>
                Não existem avaliações no período selecionado. Modifique os filtros para buscar
                novamente.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const FeedbackAnalyticsDashboard: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('satisfaction_feedback.breadcrumb_dashboard') as string,
            path: 'dashboard',
            isActive: false,
          },
          {title: 'Analytics', path: 'analytics', isActive: true},
        ]}
      >
        Dashboard Analytics
      </PageTitle>
      <Content />
    </>
  )
}

export default FeedbackAnalyticsDashboard
